import { TaxonomyIdItem } from "@amzn/taxonomy-lib";
import { getString } from "common/uistringlabels/uiStringUtils";
import { CATALOGICAL_ENDPOINT } from "config";
import { StatusCodes } from "http-status-codes";
import { AIForMeTool } from "lib/models/aiforme-models";

import { CatalogicalRequestContent } from "../../common/types/catalogical-type";
import { decodeHtmlEntityDeep } from "../helpers/htmlEntityDecode-helper";
import { BaseService } from "./base-service";
import {
  FeedbackModelRecord,
  FeedbackModelRequest,
  FilteringOptions,
  GetFeedbackResponse,
  GetFeedbacksResponse,
  GetItemResponse,
  GetItemsResponse,
  ItemModel,
  submitError,
} from "./catalogical-models";

// Match messages containing both 'taxonomyId' and 'already exists'
const DUPLICATE_TAXONOMY_ID_REGEX = /taxonomyId.+already exists/i;

export type CatalogicalDomain = "software" | "amznham" | "amznhamaux" | "aiforme" | "usecases" | "itshopfaq";

export class CatalogicalService<T extends ItemModel> extends BaseService {
  protected domain: string;

  constructor(domain: CatalogicalDomain) {
    super(CATALOGICAL_ENDPOINT + `/cat-${domain}`);

    this.domain = domain;
  }

  public async getCatalogItemByTaxonomyId(id: TaxonomyIdItem): Promise<GetItemResponse<T> | undefined> {
    const url = `/item/${id.toPlainIdWithoutType()}`;
    const response = await this.fetch(url);

    if (response.status === StatusCodes.OK) {
      const itemResponse = await response.json();
      // Decode HTML entities in the entire response object
      return decodeHtmlEntityDeep(itemResponse);
    }
    throw new Error("Failed to fetch catalog item with Taxonomy ID: " + id);
  }

  public async getItems(parameters: FilteringOptions): Promise<GetItemsResponse<T> | undefined> {
    // Pagination
    if (!parameters.pageIndex) {
      parameters.pageIndex = 0;
    }
    if (!parameters.pageSize) {
      parameters.pageSize = 20;
    }

    const queryParams = new URLSearchParams({
      limit: `${parameters.pageSize}`,
      offset: `${parameters.pageIndex * parameters.pageSize}`,
      sortBy: `${parameters.sortBy?.join(",")}`,
      sortDirection: `${parameters.sortDirection?.join(",")}`,
      filter: `${JSON.stringify(parameters.filters)}`,
    });

    if (parameters.searchText) {
      // Using * to have a 'starts with' search
      //queryParams.append("keywords", `${parameters.searchText}*`);
      //queryParams.append("fuzzy", "true");
      queryParams.append(
        "phrase",
        JSON.stringify({
          query: parameters.searchText,
          fields: ["title", "description", "tags"],
        })
      );
    }

    const url = `/items/${this.domain}/?${queryParams.toString()}`;
    const response = await this.fetch(url);
    if ([StatusCodes.OK, StatusCodes.CREATED].includes(response.status)) {
      const itemsResponse = await response.json();
      // Decode HTML entities in the entire response object
      return decodeHtmlEntityDeep(itemsResponse);
    }

    throw new Error("Failed to fetch catalog items");
  }

  public async getMyFeedbacks(): Promise<GetFeedbacksResponse | undefined> {
    const url = `/feedbacks/${this.domain}`;
    const response = await this.fetch(url);
    if (response.status === StatusCodes.OK) {
      return await response.json();
    }

    console.error("Failed to fetch feedbacks");
    return undefined;
  }

  public async getProductFeedbacks(id: TaxonomyIdItem): Promise<GetFeedbackResponse | undefined> {
    const url = `/feedback/${id.toPlainIdWithoutType()}`;
    const response = await this.fetch(url);
    if (response.status === StatusCodes.OK) {
      return await response.json();
    }

    console.error("Failed to fetch feedbacks");
    return undefined;
  }

  public async postFeedback(
    id: TaxonomyIdItem,
    feedback: FeedbackModelRequest,
    csrfToken: string
  ): Promise<FeedbackModelRecord> {
    const url = `/feedback/${id.toPlainIdWithoutType()}`;
    const response = await this.fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-TOKEN": csrfToken,
      },
      body: JSON.stringify(feedback),
    });

    if (response.status === StatusCodes.CREATED) {
      return (await response.json()) as FeedbackModelRecord;
    }
    throw new Error("Failed to post feedback");
  }

  /**
   * Posts an interaction for a catalog item
   * @param id The taxonomy ID of the item
   * @param csrfToken CSRF token for security
   * @returns A promise that resolves when the interaction is posted
   */
  public async postInteraction(id: TaxonomyIdItem, csrfToken: string): Promise<void> {
    const url = `/interactions/${id.toPlainIdWithoutType()}`;
    const response = await this.fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-TOKEN": csrfToken,
      },
      body: JSON.stringify({ interactionType: "click" }),
    });

    if (response.status !== StatusCodes.CREATED && response.status !== StatusCodes.OK) {
      console.error(`Failed to post interaction: ${response.status}`);
    }
  }

  public async submitTool(
    newTool: Partial<AIForMeTool>,
    csrfToken: string
  ): Promise<GetItemResponse<AIForMeTool> | submitError> {
    const url = `/item/aiforme`;
    const response = await this.fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-TOKEN": csrfToken,
      },
      body: JSON.stringify({
        notification: true,
        item: newTool,
      }),
    });

    if (response.status === StatusCodes.CREATED) {
      return (await response.json()) as GetItemResponse<AIForMeTool>;
    } else {
      return await this.handleSubmitError(response);
    }
  }

  public async editTool(
    newTool: Partial<AIForMeTool>,
    csrfToken: string
  ): Promise<GetItemResponse<AIForMeTool> | submitError> {
    if (!newTool.taxonomyId) {
      throw new Error("taxonomyId is required for editing a tool");
    }

    let toolId: string;
    try {
      toolId = new TaxonomyIdItem(newTool.taxonomyId).toPlainIdWithoutType();
    } catch (error) {
      throw new Error(`Invalid taxonomyId format: ${newTool.taxonomyId}`);
    }

    const url = `/item/${toolId}`;
    const response = await this.fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-TOKEN": csrfToken,
      },
      body: JSON.stringify({
        notification: true,
        item: newTool,
      }),
    });

    if (response.status === StatusCodes.OK) {
      return (await response.json()) as GetItemResponse<AIForMeTool>;
    } else {
      return await this.handleSubmitError(response);
    }
  }

  /**
   * @deprecated
   */
  public async getCatalogicalItems(parameters: CatalogicalRequestContent): Promise<GetItemsResponse<T> | undefined> {
    const queryParams = new URLSearchParams({
      limit: `${parameters.limit}`,
      offset: `${parameters.offset}`,
      sortBy: `${parameters.sortBy}`,
      sortDirection: `${parameters.sortDirection}`,
      filter: `${parameters.filter}`,
    });

    if (parameters.fuzzy != undefined && parameters.keywords != undefined) {
      queryParams.append("fuzzy", `${parameters.fuzzy}`);
      queryParams.append("keywords", `${parameters.keywords}`);
    }

    const url = `/items/${this.domain}/?${queryParams.toString()}`;
    const response = await this.fetch(url);
    if ([StatusCodes.OK, StatusCodes.CREATED].includes(response.status)) {
      const itemsResponse = await response.json();
      // Decode HTML entities in the entire response object
      return decodeHtmlEntityDeep(itemsResponse);
    }

    throw new Error("Failed to fetch catalog items");
  }

  private async handleSubmitError(response: Response): Promise<submitError> {
    if (response.status === StatusCodes.BAD_REQUEST) {
      try {
        const res = await response.json();

        //Duplicate taxonomyId
        if (res.message?.match(DUPLICATE_TAXONOMY_ID_REGEX)) {
          return { status: 400, errorMessage: getString("aiForMe.failedTool3"), cti: false };
        }
      } catch (error) {
        return { status: 500, errorMessage: getString("aiForMe.failedTool2") };
      }
    }
    return { status: response.status, errorMessage: getString("aiForMe.failedTool1") };
  }
}
